import useLocalization from "@/hooks/useLocalization";
import {
  Badge,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  DashboardChartMetrics,
  DashboardGraphScope,
  DashboardMetricTotals,
} from "../types";
import {
  addBucketLabels,
  createEmptyTotals,
  formatInteger,
  formatRevenue,
  getAcceptanceRate,
  getGuestsPerReservation,
} from "../utils";

type DashboardMetricsTableProps = {
  graphScope: DashboardGraphScope;
  metrics: DashboardChartMetrics[];
};

type SortDirection = "asc" | "desc";

type SortKey =
  | "period"
  | "reservations"
  | "accepted"
  | "rejected"
  | "pending"
  | "unanswered"
  | "acceptanceRate"
  | "guests"
  | "guestsPerReservation"
  | "revenue"
  | "cancellationNoShowRevenue";

type SortRule = {
  key: SortKey;
  direction: SortDirection;
};

type TableMetric = DashboardChartMetrics & {
  bucketLabel: string;
};

type Column = {
  align?: "right";
  key: SortKey;
  label: string;
  minWidth?: number;
  value: (metric: TableMetric) => number;
  width: number;
};

const stickyColumnSx = {
  backgroundColor: "background.paper",
  left: 0,
  position: "sticky",
  zIndex: 2,
};

const getTotals = (metrics: DashboardChartMetrics[]) =>
  metrics.reduce((totals, metric) => {
    totals.accepted += metric.accepted;
    totals.cancellationNoShowRevenue += metric.cancellationNoShowRevenue;
    totals.guests += metric.guests;
    totals.pending += metric.pending;
    totals.rejected += metric.rejected;
    totals.reservations += metric.reservations;
    totals.revenue += metric.revenue;
    totals.unanswered += metric.unanswered;

    return totals;
  }, createEmptyTotals());

const getGuestsPerReservationValue = ({
  guests,
  reservations,
}: Pick<DashboardMetricTotals, "guests" | "reservations">) =>
  reservations === 0 ? 0 : guests / reservations;

const getNextSortRules = ({
  additive,
  key,
  sortRules,
}: {
  additive: boolean;
  key: SortKey;
  sortRules: SortRule[];
}): SortRule[] => {
  const existingRule = sortRules.find((rule) => rule.key === key);
  const nextRule =
    !existingRule
      ? { key, direction: "asc" as const }
      : existingRule.direction === "asc"
      ? { key, direction: "desc" as const }
      : null;

  if (!additive) return nextRule ? [nextRule] : [];

  const remainingRules = sortRules.filter((rule) => rule.key !== key);

  return nextRule ? [...remainingRules, nextRule] : remainingRules;
};

const sortMetrics = ({
  columns,
  metrics,
  sortRules,
}: {
  columns: Column[];
  metrics: TableMetric[];
  sortRules: SortRule[];
}) => {
  if (sortRules.length === 0) return metrics;

  const columnMap = new Map(columns.map((column) => [column.key, column]));

  return [...metrics].sort((left, right) => {
    for (const { direction, key } of sortRules) {
      const column = columnMap.get(key);
      if (!column) continue;

      const leftValue = column.value(left);
      const rightValue = column.value(right);
      const result = leftValue - rightValue;

      if (result !== 0) return direction === "asc" ? result : -result;
    }

    return left.bucketStart.getTime() - right.bucketStart.getTime();
  });
};

export default function DashboardMetricsTable({
  graphScope,
  metrics,
}: DashboardMetricsTableProps) {
  const { adapterLocale, t } = useLocalization();
  const [sortRules, setSortRules] = useState<SortRule[]>([]);
  const columns = useMemo<Column[]>(
    () => [
      {
        key: "period",
        label: t("dashboard:period"),
        minWidth: 96,
        value: (metric) => metric.bucketStart.getTime(),
        width: 96,
      },
      {
        align: "right",
        key: "reservations",
        label: t("dashboard:reservations"),
        value: (metric) => metric.reservations,
        width: 76,
      },
      {
        align: "right",
        key: "accepted",
        label: t("dashboard:accepted"),
        value: (metric) => metric.accepted,
        width: 68,
      },
      {
        align: "right",
        key: "rejected",
        label: t("dashboard:rejected"),
        value: (metric) => metric.rejected,
        width: 68,
      },
      {
        align: "right",
        key: "pending",
        label: t("dashboard:pending"),
        value: (metric) => metric.pending,
        width: 68,
      },
      {
        align: "right",
        key: "unanswered",
        label: t("dashboard:unanswered"),
        value: (metric) => metric.unanswered,
        width: 76,
      },
      {
        align: "right",
        key: "acceptanceRate",
        label: t("dashboard:acceptance"),
        value: getAcceptanceRate,
        width: 74,
      },
      {
        align: "right",
        key: "guests",
        label: t("dashboard:guests"),
        value: (metric) => metric.guests,
        width: 66,
      },
      {
        align: "right",
        key: "guestsPerReservation",
        label: t("dashboard:averageGuests"),
        value: getGuestsPerReservationValue,
        width: 72,
      },
      {
        align: "right",
        key: "revenue",
        label: t("dashboard:revenue"),
        value: (metric) => metric.revenue,
        width: 104,
      },
      {
        align: "right",
        key: "cancellationNoShowRevenue",
        label: t("dashboard:otherRevenue"),
        value: (metric) => metric.cancellationNoShowRevenue,
        width: 104,
      },
    ],
    [t],
  );
  const tableRows = useMemo(
    () =>
      sortMetrics({
        columns,
        metrics: addBucketLabels(metrics, graphScope, adapterLocale),
        sortRules,
      }),
    [adapterLocale, columns, graphScope, metrics, sortRules],
  );
  const totals = getTotals(metrics);
  const sortRuleMap = new Map(sortRules.map((rule, index) => [rule.key, {
    ...rule,
    priority: index + 1,
  }]));

  const handleSort = (key: SortKey, additive: boolean) => {
    setSortRules((prevRules) =>
      getNextSortRules({
        additive,
        key,
        sortRules: prevRules,
      }),
    );
  };

  const renderMetricCells = (metric: DashboardMetricTotals) => (
    <>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatInteger(metric.reservations)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatInteger(metric.accepted)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatInteger(metric.rejected)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatInteger(metric.pending)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatInteger(metric.unanswered)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {`${Math.round(getAcceptanceRate(metric))}%`}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatInteger(metric.guests)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {getGuestsPerReservation(metric)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatRevenue(metric.revenue)}
      </TableCell>
      <TableCell align="right" sx={{ px: 1, whiteSpace: "nowrap" }}>
        {formatRevenue(metric.cancellationNoShowRevenue)}
      </TableCell>
    </>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ overflowX: "auto", width: "100%" }}
      variant="outlined"
    >
      <Table
        size="small"
        sx={{
          minWidth: 880,
          tableLayout: "fixed",
          "& th, & td": {
            borderColor: "divider",
          },
        }}
      >
        <colgroup>
          {columns.map((column) => (
            <col
              key={column.key}
              style={{
                minWidth: column.minWidth ?? column.width,
                width: column.width,
              }}
            />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const sortRule = sortRuleMap.get(column.key);
              const isPeriodColumn = column.key === "period";

              return (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    ...(isPeriodColumn ? stickyColumnSx : {}),
                    px: 1,
                    whiteSpace: "normal",
                    width: column.width,
                  }}
                >
                  <TableSortLabel
                    active={Boolean(sortRule)}
                    direction={sortRule?.direction ?? "asc"}
                    onClick={(event) => {
                      handleSort(column.key, event.shiftKey);
                    }}
                    sx={{
                      minWidth: 0,
                      width: "100%",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline",
                      }}
                    >
                      {sortRule && sortRules.length > 1 ? (
                        <Badge
                          badgeContent={sortRule.priority}
                          color="primary"
                          sx={{ "& .MuiBadge-badge": { right: -12, top: 2 } }}
                        >
                          {column.label}
                        </Badge>
                      ) : (
                        column.label
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {tableRows.map((metric) => (
            <TableRow key={metric.bucketStart.toISOString()} hover>
              <TableCell
                component="th"
                scope="row"
                sx={{
                  ...stickyColumnSx,
                  fontWeight: 500,
                  px: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {metric.bucketLabel}
              </TableCell>
              {renderMetricCells(metric)}
            </TableRow>
          ))}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TableCell
              sx={{
                ...stickyColumnSx,
                fontWeight: 700,
                px: 1,
                whiteSpace: "nowrap",
                zIndex: 3,
              }}
            >
              {t("dashboard:total")}
            </TableCell>
            {renderMetricCells(totals)}
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
